import { Injectable, type Signal, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';

export interface UserProfile {
  sub: string;
  email: string;
  name: string;
  givenName: string;
  familyName: string;
  preferredUsername: string;
  roles: string[];
  initials: string;
}

interface KeycloakTokenClaims {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakSignal: Signal<{ type: KeycloakEventType }> = inject(
    KEYCLOAK_EVENT_SIGNAL,
  );

  /**
   * Whether the user is currently authenticated.
   */
  isLoggedIn(): boolean {
    return !!this.keycloak.authenticated;
  }

  /**
   * Redirect to Keycloak login page.
   */
  async login(redirectUri?: string): Promise<void> {
    await this.keycloak.login({
      redirectUri: redirectUri ?? `${window.location.origin}/dashboard`,
    });
  }

  /**
   * Logout from Keycloak and redirect.
   */
  async logout(redirectUri?: string): Promise<void> {
    await this.keycloak.logout({
      redirectUri: redirectUri ?? `${window.location.origin}/home`,
    });
  }

  /**
   * Get the current access token.
   */
  getToken(): string | undefined {
    return this.keycloak.token;
  }

  /**
   * Try to refresh the token. Returns true if the token was refreshed.
   */
  async updateToken(minValidity = 30): Promise<boolean> {
    try {
      return await this.keycloak.updateToken(minValidity);
    } catch {
      return false;
    }
  }

  /**
   * Get the current user profile from JWT claims.
   */
  getUserProfile(): UserProfile | null {
    if (!this.keycloak.authenticated || !this.keycloak.tokenParsed) {
      return null;
    }

    const token = this.keycloak.tokenParsed as KeycloakTokenClaims;
    const givenName = token.given_name ?? '';
    const familyName = token.family_name ?? '';
    const name = [givenName, familyName].filter(Boolean).join(' ') || 'User';

    const realmRoles = token.realm_access?.roles ?? [];
    const resourceAccess = token.resource_access;
    const clientRoles = resourceAccess
      ? Object.values(resourceAccess).flatMap((r) => r.roles ?? [])
      : [];

    const initials =
      [givenName, familyName]
        .filter(Boolean)
        .map((n) => n.charAt(0).toUpperCase())
        .join('') || 'U';

    return {
      sub: token.sub ?? '',
      email: token.email ?? '',
      name,
      givenName,
      familyName,
      preferredUsername: token.preferred_username ?? '',
      roles: [...realmRoles, ...clientRoles],
      initials,
    };
  }

  /**
   * Check if the last keycloak event is a token expiry.
   */
  isTokenExpired(): boolean {
    return this.keycloakSignal().type === KeycloakEventType.TokenExpired;
  }
}
